import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge, ConfirmDelete, Field, PageHeader, SelectField } from "@/components/common";
import { api, formatApiError } from "@/lib/api";
import { useFormState, useList } from "@/lib/hooks";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/constants";

const NewMemberDialog = ({ open, onOpenChange, onSaved }) => {
  const { form, setForm, set } = useFormState({ name: "", email: "", password: "", role: "technicien" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("Collaborateur ajouté");
      setForm({ name: "", email: "", password: "", role: "technicien" });
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F1F3D] border-cyan-400/20" data-testid="member-dialog">
        <DialogHeader>
          <DialogTitle>Ajouter un collaborateur</DialogTitle>
          <DialogDescription>Communiquez-lui son mot de passe provisoire ; il pourra le changer ensuite.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Nom *"><Input required value={form.name} onChange={set("name")} data-testid="member-name-input" /></Field>
          <Field label="Email *"><Input required type="email" value={form.email} onChange={set("email")} data-testid="member-email-input" /></Field>
          <Field label="Mot de passe provisoire * (8 caractères min.)"><Input required minLength={8} value={form.password} onChange={set("password")} data-testid="member-password-input" /></Field>
          <Field label="Rôle"><SelectField value={form.role} onChange={set("role")} options={ROLES} testid="member-role-select" /></Field>
          <Button type="submit" className="w-full" data-testid="member-submit-button">Créer le compte</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const PasswordCard = () => {
  const { form, setForm, set } = useFormState({ current_password: "", new_password: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", form);
      toast.success("Mot de passe modifié");
      setForm({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <form onSubmit={submit} className="dt-card min-w-0 space-y-4 p-5" data-testid="password-form">
      <h2 className="text-lg font-semibold text-white">Mon mot de passe</h2>
      <Field label="Actuel"><Input type="password" required value={form.current_password} onChange={set("current_password")} data-testid="current-password-input" /></Field>
      <Field label="Nouveau (8 caractères min.)"><Input type="password" required minLength={8} value={form.new_password} onChange={set("new_password")} data-testid="new-password-input" /></Field>
      <Button type="submit" variant="secondary" data-testid="change-password-button">Modifier</Button>
    </form>
  );
};

export default function Team() {
  const { user } = useAuth();
  const { items, reload } = useList("/users");
  const [open, setOpen] = useState(false);
  const [del, setDel] = useState(null);
  const isAdmin = user?.role === "admin";
  const remove = async () => {
    await api.delete(`/users/${del.id}`);
    toast.success("Compte supprimé");
    setDel(null);
    reload();
  };

  return (
    <>
      <PageHeader title="Équipe" subtitle="Comptes des collaborateurs DTech42">
        {isAdmin && <Button onClick={() => setOpen(true)} className="dt-glow" data-testid="new-member-button"><Plus className="mr-1 h-4 w-4" />Ajouter un collaborateur</Button>}
      </PageHeader>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_340px]">
        <div className="min-w-0 space-y-3" data-testid="team-list">
          {items.map((u) => (
            <div key={u.id} className="dt-card flex items-center justify-between gap-3 p-4" data-testid={`member-row-${u.id}`}>
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cyan-400/15 font-display font-semibold text-cyan-300">{(u.name || u.email)[0].toUpperCase()}</div>
                <div className="min-w-0"><p className="truncate font-medium text-white">{u.name}</p><p className="truncate text-xs text-slate-400">{u.email}</p></div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Badge value={u.role} map={ROLES} />
                {isAdmin && u.id !== user.id && <Button size="icon" variant="ghost" onClick={() => setDel(u)} data-testid={`member-delete-${u.id}`} className="hover:text-red-300"><Trash2 className="h-4 w-4" /></Button>}
              </div>
            </div>
          ))}
        </div>
        <PasswordCard />
      </div>
      <NewMemberDialog open={open} onOpenChange={setOpen} onSaved={reload} />
      <ConfirmDelete open={!!del} onOpenChange={(o) => !o && setDel(null)} onConfirm={remove} label="ce compte" />
    </>
  );
}
